import {
  NoticeModal,
  Stack,
  Text,
  Input,
  HookFormErrorMessage,
} from '@pairy/pds-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';

import { SetState } from '@/types/helper';

import Preview from '../../../../common/CreateFileModal/FileUpload/Preview';
import Loading from '../../../../common/Loading';

import * as Styled from './index.styles';
import ThumbnailButton from './ThumbnailButton';
import useHandleEditFile from './useHandleEditFile';

interface Props {
  isOpen: boolean;
  setIsOpen: SetState<boolean>;
  fileId: number;
  fileName: string;
  fileThumbnail: string;
}

interface Inputs {
  fileName: string;
}

const EditFileModal = ({
  isOpen,
  setIsOpen,
  fileId,
  fileName,
  fileThumbnail,
}: Props) => {
  const handleEditModalClose = () => {
    setIsOpen(false);
  };

  const {
    file,
    fileUploadRef,
    isFileChange,
    isLoading,
    handleEditButtonClick,
    handleFileChange,
    handleFileUploadClick,
  } = useHandleEditFile({ fileId, handleEditModalClose });

  const {
    register,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid },
    trigger,
  } = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: {
      fileName: fileName,
    },
  });

  const getIsFileChangeValid = () => {
    return typeof file !== 'undefined' && isDirty ? isValid : true;
  };

  const isEditButtonDisabled = useMemo(() => {
    return isFileChange ? getIsFileChangeValid() : isDirty && isValid;
  }, [isDirty, isValid, isFileChange, file]);

  const onDrop = (acceptedFiles: File[]) => {
    const fakeEvent = { target: { files: acceptedFiles } } as any;
    handleFileChange(fakeEvent);

    const uploaded = acceptedFiles[0];
    if (uploaded?.name.endsWith('.pdf')) {
      setValue('fileName', uploaded.name.replace('.pdf', ''));
      trigger();
    }
  };

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    multiple: false,
    noClick: true,
  });

  useEffect(() => {
    if (file) {
      setValue('fileName', file.name.replace('.pdf', ''));
      trigger();
    }
  }, [file]);

  return (
    <NoticeModal
      isOpen={isOpen}
      onOpenChange={(open) => setIsOpen(open)}
      title="파일 수정하기"
      buttonType="success"
      buttonText="수정완료"
      buttonDisabled={!isEditButtonDisabled}
      handleButtonClick={() =>
        handleEditButtonClick({ fileName: getValues('fileName') })
      }
    >
      {isLoading && (
        <Styled.LoadingWrapper justifyContent="center" alignItems="center">
          <Loading />
        </Styled.LoadingWrapper>
      )}
      <Stack {...getRootProps()} gap={32} direction="column">
        <Stack gap={16} direction="column">
          <Text variant="md-semibold">
            새로운 파일로 교체하기
            <br />
            <Text variant="sm-semibold" color="cerise_400">
              100MB 이하의 파일만 업로드 할 수 있습니다
            </Text>
          </Text>
          {file ? (
            <Preview
              file={file}
              filename={file.name}
              onClickChangeFile={handleFileUploadClick}
            />
          ) : (
            <ThumbnailButton
              fileThumbnail={fileThumbnail}
              handleFileThumbnailClick={handleFileUploadClick}
              isDragActive={isDragActive}
            />
          )}

          <input
            type="file"
            ref={fileUploadRef}
            width="100%"
            accept="application/pdf"
            style={{ opacity: 0 }}
            onChange={handleFileChange}
          />
        </Stack>

        {file && (
          <Styled.GrayBox
            gap={8}
            padding={16}
            direction="column"
            backgroundColor="zinc_50"
          >
            <Text variant="lg-semibold" color="zinc_500">
              기존 파일을 교체하면
            </Text>
            <Stack gap={6} alignItems="center">
              <Text variant="md-medium" color="zinc_500">
                &bull;
              </Text>
              <Text variant="md-medium" color="zinc_500">
                이전 파일에 설정된{' '}
                <b>버튼, 문의양식, 메시지 박스, 동영상은 적용되지 않습니다.</b>
              </Text>
            </Stack>
            <Stack gap={6} alignItems="center">
              <Text variant="md-medium" color="zinc_500">
                &bull;
              </Text>
              <Text variant="md-medium" color="zinc_500">
                <b>기존의 세일즈클루 링크가 유지</b>되며, 링크를 클릭할 시{' '}
                <b>새로 교체된 파일이 보여집니다.</b>
              </Text>
            </Stack>
            <Stack gap={6} alignItems="center">
              <Text variant="md-medium" color="zinc_500">
                &bull;
              </Text>
              <Text variant="md-medium" color="zinc_500">
                이전 파일의 열람 기록은{' '}
                <b>
                  [열람 분석] &gt; [고객 반응 분석] 상단에서 확인하실 수
                  있습니다.
                </b>
              </Text>
            </Stack>
          </Styled.GrayBox>
        )}

        <label>
          <Stack
            gap={16}
            direction="column"
            onClick={(e) => e.stopPropagation()}
          >
            <Text variant="md-semibold">
              파일 이름
              <br />
              <Text variant="sm-semibold" color="zinc_400">
                파일을 업로드하면 해당 파일명이 자동으로 입력됩니다.
              </Text>
            </Text>
            <Input
              width="100%"
              isError={Boolean(errors.fileName)}
              {...register('fileName', {
                required: '이름을 입력해주세요.',
              })}
            />
          </Stack>
          <HookFormErrorMessage errors={errors} name="fileName" />
        </label>
      </Stack>
    </NoticeModal>
  );
};

export default EditFileModal;
